import { useFormik } from 'formik';
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../component/colors';
import { DropDownWithLabel } from '../../../../component/dropDown';
import { ImagePicker } from '../../../../component/imagePicker';
import { Input } from '../../../../component/input';
import { AddModal } from '../../../../component/modal';
import { TextAreaWithLabel } from '../../../../component/textArea';
import {
  useEditProductsMutation,
  useGetCategoriesQuery
} from '../../../../store/shopping/api';
import { IStoreProduct } from '../../../../store/shopping/interface';
import { showErrorToast, showSuccessToast } from '../../../../utils/toast';
import { addProductValidation } from '../../validation';

type IEditProduct = {
  show?: boolean;
  product: IStoreProduct;
  setShow: Dispatch<SetStateAction<IStoreProduct | null>>;
};
type FormikValues = {
  name: string;
  category: {
    name: string;
    index: number;
  };
  description: string;
  price: number;
  stock: number | string;
  file: File[] | null;
};
export const EditProduct: FC<IEditProduct> = ({ setShow, product }) => {
  const [editProduct, { isLoading: isEditing }] = useEditProductsMutation();
  const { data: allCategories } = useGetCategoriesQuery();
  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };
  const categoryList = useMemo(() => {
    return allCategories?.data.map((category) => category.name) ?? [];
  }, [allCategories]);

  const handleAddProducts = (values: FormikValues) => {
    if (!allCategories?.data) return;
    const formData = new FormData();
    if (values.name.toLowerCase() !== product.name.toLowerCase()) {
      formData.append('name', values.name);
    }
    if (values.price !== product.price) {
      formData.append('price', values.price.toString());
    }
    if (values.stock !== product.stock) {
      formData.append('stock', values.stock.toString());
    }
    if (values.description !== product?.description) {
      formData.append('description', values.name);
    }
    if (values.file !== null) {
      values.file.forEach((file) => {
        formData.append('file', file);
      });
    }
    if (
      values.category.name.toLowerCase() !== product.category.name.toLowerCase()
    ) {
      formData.append(
        'category',
        allCategories.data[values.category.index]._id
      );
    }

    editProduct({ payload: formData, id: product._id })
      .unwrap()
      .then((res) => {
        showSuccessToast(res.message);
        setShow(null);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  const { values, errors, touched, setFieldValue, handleChange, handleSubmit } =
    useFormik({
      initialValues: {
        name: product.name,
        category: { name: product.category.name, index: 0 },
        price: product.price,
        stock: product.stock,
        description: product.description,
        file: null
      },
      validationSchema: addProductValidation,
      onSubmit: handleAddProducts
    });

  return (
    <AddModal
      title="Edit Product"
      disableBtn={isEditing}
      btnTxt={isEditing ? 'Loading...' : 'Edit Product'}
      btnClick={handleSubmit}
      closeModal={() => setShow(null)}
      isLoading={isEditing}
    >
      <Container>
        <Row>
          <CustomInput
            labelText="Name"
            value={values.name}
            placeholder={'Enter product name'}
            onChange={handleChange('name')}
            errorMessage={touched.name ? errors.name : ''}
          />

          <CustomDropDown
            label="Category"
            value={values.category.name}
            onSelect={(item, index) => {
              setFieldValue('category', { name: item, index });
            }}
            options={categoryList}
            errorMessage={
              touched.category && errors.category ? '*required' : ''
            }
          />
        </Row>

        <Row>
          <CustomInput
            labelText={'Price'}
            value={values.price}
            onChange={handleChange('price')}
            errorMessage={touched.price ? errors.price : ''}
          />
          <CustomInput
            labelText={'Stock'}
            value={values.stock}
            type={'number'}
            min={0}
            onChange={handleChange('stock')}
            errorMessage={touched.stock ? errors.stock : ''}
          />
        </Row>
        <CustomTextAreaWithLabel
          labelText="Description"
          placeholder="Enter description"
          value={values.description}
          onChange={handleChange('description')}
          errorMessage={touched.description ? errors.description : ''}
        />
        <CustomImageUpload
          initialValue={product.file.map((item) => item.url)}
          onFilePicked={handleFilePicked}
        />
      </Container>
    </AddModal>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  padding-bottom: 32px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 500px) {
    display: block;
  }
`;

const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomImageUpload = styled(ImagePicker)``;

const CustomTextAreaWithLabel = styled(TextAreaWithLabel)`
  margin-bottom: 16px;
`;
